import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import api from 'services/api';
import * as Styled from './styles';
import Input from '../Input';
import { cooperplaceApi } from 'services/api';
import Loader from 'v3/components/Loader';
import Text from 'v3/components/Text';
import { useSnackbar } from 'v3/components/Snackbar';

function InputAddress(
  {
    label,
    error,
    country = 'br',
    name,
    onSelectedAddress,
    value,
    onChange,
    typesRestriction = [],
    countryRestriction = null,
    removeCountryRestriction = false,
    id,
    ...props
  },
  ref
) {

  const [inputValue, setInputValue] = useState(value);
  const [countryValue, setCountryValue] = useState(country);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const snackbar = useSnackbar();

  const fetchGoogleAddressDetails = async (data) => {
    try {
      const res = await cooperplaceApi.get('google/address-autocomplete-details', {
        params: { placeId: data.place_id }
      });
      return res.data;
    } catch (error) {
      const message =
        (error && error.response && error.response.data && error.response.data.error)
          ? error.response.data.error
          : 'Erro ao buscar detalhes do endereço.';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      return null;
    }
  };

  const fetchGoogleAddress = async (searchValue, country) => {
    try {
      const res = await cooperplaceApi.get('google/address-autocomplete', {
        params: {
          types: typesRestriction,
          input: searchValue,
          country: removeCountryRestriction ? null : country
        }
      });
      setFetchError(null);        
      setSuggestions(res.data);
    } catch (error) {
      const message =
        (error && error.response && error.response.data && error.response.data.error)
          ? error.response.data.error
          : 'Falha ao consultar o Google Maps. Verifique a chave/billing.';
      setFetchError(message);    
      setSuggestions(null);      
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    };
  }

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    setSuggestions(null);
    setFetchError(null);      
    setCountryValue(country);
  }, [country]);

  const debouncedUpdate = useCallback(
    debounce((val, country) => {
      fetchSuggestions(val, country);
    }, 2000),
    [countryValue]
  );

  const handleChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setSuggestions(null);
    setFetchError(null);  
    setLoading(true);
    debouncedUpdate(newValue, countryValue);
    if (onChange) onChange(event);
  };

  const fetchSuggestions = async (searchValue, country) => {
    if (searchValue.trim() === '') {
      setSuggestions(null);
      setFetchError(null);
      setLoading(false);
      return;
    }
    await fetchGoogleAddress(searchValue, country);
  };

  const handleSelectSuggestion = (suggestion) => {
    setInputValue(suggestion.description);
    setSuggestions(null);
    handleSelected(suggestion);
  };

  async function handleSelected(data) {
    const addressDetails = await fetchGoogleAddressDetails(data);
    if (!addressDetails) return;
    const locale = {};

    async function fetchProvinces(search, uf, country) {
      try {
        const response = await api.get('cities', { params: { search, uf, country } });
        return response.data;
      } catch (ex) {
        return null;
      }
    }

    if (addressDetails.address_components) {
      addressDetails.address_components.forEach(address => {
        if (address.types.includes('administrative_area_level_1')) {
          locale.province = address.short_name;
          locale.province_name = address.long_name;
        }

        if (address.types.includes('locality')) {
          locale.locality = address.long_name;
        }

        if (address.types.includes('administrative_area_level_2')) {
          locale.administrative_area_level_2 = address.long_name;
        }

        if (address.types.includes('postal_code')) {
          locale.zip_code = address.long_name;
        }

        if (address.types.includes('sublocality_level_1')) {
          locale.neighborhood = address.long_name;
        }

        if (address.types.includes('street_number')) {
          locale.number = address.long_name;
        }

        if (address.types.includes('country')) {
          locale.country_abbreviation = address.short_name.toLowerCase();
        }

        if (address.types.includes('route')) {
          locale.address = address.long_name;
        }
      });

      locale.lat = addressDetails.geometry.location.lat;
      locale.lng = addressDetails.geometry.location.lng;
      locale.formatted_address = addressDetails.formatted_address;
      locale.name = addressDetails.name;
      locale.isEstablishment = addressDetails.types.includes('establishment');
      locale.address =
        locale.address ||
        addressDetails.name ||
        data.description ||
        addressDetails.formatted_address ||
        '';

      if (locale.country_abbreviation !== 'br') {
        locale.city = locale.locality;
        locale.province = locale.province_name;
      } else {
        locale.city = locale.administrative_area_level_2;
      }

      if (addressDetails.name && locale.isEstablishment) {
        locale.formatted_address = `${addressDetails.name} - ${locale.formatted_address}`;
      }
    }

    if (locale.city && locale.province) {
      const city = await fetchProvinces(locale.city, locale.province, locale.country_abbreviation);

      if (city?.length) {
        locale.city_id = city[0]?.name === locale.city ? city[0].id : null;
      } else {
        locale.city_id = locale.city === city?.name ? city.id : null;
      }
    }

    onSelectedAddress(locale);
  }

  return (
    <Styled.Container>
      {label && (
        <Styled.Label color="#464e5f" type="label" weight="500">
          {label}
        </Styled.Label>
      )}
      <Styled.InputWrapper $hasError={!!error}>
        <Input
          ref={ref}
          type="text"
          name={name}
          id={id}
          value={inputValue}
          onChange={handleChange}
          {...props}
        />
      </Styled.InputWrapper>

      {error && (
        <Styled.ErrorText color="danger" type="little">
          {error}
        </Styled.ErrorText>
      )}

      {loading ? (
        <Styled.SuggestionsListLoading>
          <Text weight={500} color={'#888'}>Carregando </Text>
          <Loader loading={loading} />
        </Styled.SuggestionsListLoading>
      ) : (
        <>
          {suggestions?.length > 0 ? (
            <Styled.SuggestionsList>
              {suggestions.map((suggestion, index) => (
                <Styled.SuggestionItem
                  key={index}
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  {suggestion.description}
                </Styled.SuggestionItem>
              ))}
            </Styled.SuggestionsList>
          ) : suggestions?.length === 0 ? (
            <Styled.SuggestionsListLoading>
              <Text weight={500} color={'#888'}>Endereço não encontrado</Text>
              <Loader loading={loading} />
            </Styled.SuggestionsListLoading>
          ) : null}
        </>
      )}
    </Styled.Container>
  );
}

export default React.forwardRef(InputAddress)
